/**
 * PII sanitization for analytics event parameters.
 *
 * Defense-in-depth: strips emails, JWTs, and sensitive keys from event params
 * before they're sent to GA4. Developers should also avoid passing PII manually.
 */

import type { EventParams } from './events';

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// JWT: three base64url segments separated by dots
const JWT_REGEX = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'secret',
  'authorization',
  'cookie',
  'credit_card',
  'ssn',
  'access_token',
  'refresh_token',
  'api_key',
]);

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key.toLowerCase());
}

function sanitizeValue(value: string): string {
  return value.replace(EMAIL_REGEX, '[REDACTED_EMAIL]').replace(JWT_REGEX, '[REDACTED_TOKEN]');
}

/**
 * Sanitizes event parameters by:
 * 1. Removing keys that match sensitive key names
 * 2. Stripping email addresses from string values
 * 3. Stripping JWT tokens from string values
 */
export function sanitizeParams(params: EventParams): EventParams {
  const result: EventParams = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;

    if (isSensitiveKey(key)) {
      result[key] = '[REDACTED]';
      continue;
    }

    if (typeof value === 'string') {
      result[key] = sanitizeValue(value);
    } else {
      result[key] = value;
    }
  }

  return result;
}
